<script lang="ts">
  import Avatar from "$components/+blocks/Avatar/Avatar.svelte";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import { thread } from "$stores/thread";
  import { communitySlug } from "$stores/community";
  import { goto } from "$app/navigation";
  import { page } from "$app/stores";
  import { replyUrl, threadURL } from "$utils/urls";
  import { getTimeAgo, parseDateTime } from "app/utils/datetimes";
  import autoAnimate from "@formkit/auto-animate";
  import { getMediaQuery } from "$utils/mediaQuery";
  import { replyPanel } from "$stores/replyPanel";
  import { slugify } from "$utils/strings";
  import type { ThreadPost } from "@outverse-video/outverse-api-client-lib";
  import type { ThreadUrlParams } from "app/models/forums";
  import { RA, ReplyingTo } from "$components/+blocks/OVIcon";

  export let reply: ThreadPost;

  const isDesktop = getMediaQuery("desktop");
  const postTime = getTimeAgo(
    parseDateTime(reply.created, "datetime is invalid")
  )?.toLocaleString();

  $: parentThread = reply.thread as unknown as ThreadUrlParams;

  $: threadLink = threadURL({
    communitySlug: $communitySlug,
    spaceUuid: parentThread.forumSpace.uuid,
    spaceSlug: slugify(parentThread.forumSpace.title),
    threadUuid: parentThread.uuid,
    threadSlug: slugify(parentThread.title),
  });

  $: replyLink = replyUrl({
    communitySlug: $communitySlug,
    spaceUuid: parentThread.forumSpace.uuid,
    spaceSlug: slugify(parentThread.forumSpace.title),
    threadUuid: parentThread.uuid,
    threadSlug: slugify(parentThread.title),
    replySeqNum: reply.seqNum.toString(),
  });

  function handleReplyNavigation() {
    // If the reply is in the current thread, scroll to the post
    if (threadLink === $page.url.pathname) {
      thread.goToPostByUUID(reply.uuid);
    }
    // Otherwise, go to the thread page and scroll to the post
    else {
      goto(replyLink);
    }
    // close on mobile and tablet when navigating to the reply
    if (!$isDesktop) {
      replyPanel.hide();
    }
  }
</script>

<!-- &:active:not(:focus-within)] is used to prevent the border from changing color on active state when the user clicks on the inner elements -->
<!-- [&:hover:has(button:hover)] is used to change the background color when the user hovers over the inner "replying to" button -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  class="relative block cursor-pointer overflow-hidden rounded-md border-[0.5px] border-black/10 p-3 transition-colors hover:bg-gray-200 dark:border-white/15 dark:hover:bg-white/5 [&:active:not(:focus-within)]:border-community-primary [&:hover:has(button:hover)]:bg-white/4"
  on:click={handleReplyNavigation}
  on:keydown={handleReplyNavigation}
  use:autoAnimate={{ duration: 170 }}
>
  <div class="flex items-center gap-1.5 text-xs">
    <Avatar
      size="size-3.5"
      radius="rounded-sm"
      mainImage={reply.author.profileImage}
      backgroundColor={reply.author.profileColor}
      label={reply.author.handle[0]}
    />
    <span class="text-gray-500 dark:text-gray-200">
      {reply.author.handle}
    </span>
    <span class="text-gray-400 dark:text-gray-400/60"> {postTime} </span>
    <div class="ml-auto flex items-center gap-1 pl-2">
      <span class="text-xxs font-medium text-gray-500 dark:text-gray-400">View reply</span>
      <OVIcon icon={RA} size="size-2.5" class="text-gray-500/60 dark:text-gray-400" />
    </div>
  </div>

  <!-- if the current page is not the thread page that includes the reply do not show the replying to button -->
  {#if reply.replyToPostUuid && $page.url.pathname.includes(threadLink)}
    <button
      type={"button"}
      class="mt-2 inline-flex items-center gap-1 whitespace-nowrap rounded border-[0.5px] border-transparent bg-gray-300/40 px-1 py-0.5 text-xxs font-medium text-gray-500 transition-colors hover:bg-gray-300/80 active:border-community-primary dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-gray-100"
      on:click|preventDefault|stopPropagation={() => {
        if (reply.replyToPostUuid) {
          thread.goToPostByUUID(reply.replyToPostUuid);
        }
      }}
    >
      <OVIcon icon={ReplyingTo} class="inline-block" size="size-3" />
      Replying to
      <!-- check for author, parent post may have been deleted -->
      {#if reply.replyToPostAuthor}
        <Avatar
          size="size-3"
          mainImage={reply.replyToPostAuthor?.profileImage}
          backgroundColor={reply.replyToPostAuthor.profileColor}
          label={reply.replyToPostAuthor.handle && reply.replyToPostAuthor.handle[0]}
          wrapperClasses="ml-0.5 rounded-sm"
        />

        {reply.replyToPostAuthor.handle}
      {:else}
        [deleted]
      {/if}
    </button>
  {/if}

  <p class="mt-3 line-clamp-4 text-xs font-medium leading-[160%] text-gray-500 dark:text-gray-300">
    {reply.text}
  </p>
  {#if reply.repliesCount > 0}
    <span class="mt-2 block whitespace-nowrap text-xs text-blue-300">
      {reply.repliesCount}
      {reply.repliesCount > 1 ? "replies" : "reply"}
    </span>
  {/if}
</div>
